export const EFFECTIVE_DATE = 'August 5, 2026';
export const LAST_UPDATED = 'August 5, 2026';

export const INTRO =
  'Welcome to Nothing To Wear. These Terms and Conditions ("Terms") form a legally binding agreement between you ("User," "you," or "your") and Nothing To Wear Club Inc. (operating as "Nothing To Wear") ("Nothing To Wear," "we," "us," or "our"), a company operating in British Columbia, Canada. These Terms govern your access to and use of our website located at nothingtowearclub.ca, our mobile application(s) (collectively, the "Platform"), and all related services that allow Users to list, lend, rent, and buy and sell fashion items on a peer-to-peer basis (the "Services").';

export const DISCLAIMER =
  'BY CREATING AN ACCOUNT, CLICKING "I AGREE," OR OTHERWISE ACCESSING OR USING THE PLATFORM, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND OUR PRIVACY POLICY. IF YOU DO NOT AGREE, DO NOT USE THE PLATFORM.';

export const SECTIONS = [
  {
    number: '1',
    title: 'Acceptance of Terms',
    blocks: [
      {
        p:
          'These Terms apply to every visitor, registered User, Lender, Seller, Renter, and Buyer who accesses or uses the Platform (each, a "User" and collectively, "Users"). By using the Platform you also agree to our Privacy Policy, Community Guidelines, and any additional terms disclosed to you at the point of a specific transaction (for example, rental-specific terms shown at checkout), all of which are incorporated into these Terms by reference.',
      },
      {
        p:
          'You must read and, where prompted, expressly accept these Terms before completing registration or any transaction. Where these Terms conflict with a listing-specific or transaction-specific disclosure required by law, the more specific, legally required disclosure will govern that transaction.',
      },
    ],
  },
  {
    number: '2',
    title: 'Definitions',
    blocks: [
      {
        defList: [
          {
            term: '"Platform"',
            text:
              ' means the Nothing To Wear website and mobile application(s), together with all related features and Services.',
          },
          {
            term: '"Lender"/"Seller"',
            text:
              ' means a User who lists a fashion item for rental (Lender) or sale (Seller) through the Platform.',
          },
          {
            term: '"Renter"/"Buyer"',
            text: ' means a User who rents (Renter) or purchases (Buyer) an item listed by another User.',
          },
          {
            term: '"Listing"',
            text:
              ' means an item posted on the Platform for rental or sale, together with its description, photographs, price, and availability.',
          },
          {
            term: '"Transaction"',
            text:
              ' means a rental or purchase agreement formed directly between a Lender/Seller and a Renter/Buyer through the Platform.',
          },
          {
            term: '"Rental Period"',
            text:
              ' means the agreed period during which a Renter is entitled to possess and use a rented item, as shown at checkout.',
          },
          {
            term: '"Content"',
            text: ' means text, images, listings, reviews, messages, and other material submitted to the Platform by a User.',
          },
          {
            term: '"Consumer Protection BC"',
            text: ' refers to the regulator responsible for administering the BPCPA in British Columbia.',
          },
        ],
      },
    ],
  },
  {
    number: '3',
    title: 'Eligibility and Account Registration',
    blocks: [
      { h3: '3.1 Who May Use the Platform' },
      { p: 'To register and transact on the Platform, you must:' },
      {
        list: [
          'be at least 18 years old and have reached the age of majority in your province or territory of residence;',
          'be a resident of Canada with a valid Canadian shipping/pickup address, unless we expressly permit otherwise;',
          'have the legal capacity to enter into a binding contract; and',
          'not be barred from using the Platform under applicable Canadian law or a prior suspension by us.',
        ],
      },
      { h3: '3.2 Account Information' },
      {
        p:
          'You agree to provide accurate, current, and complete information during registration and to keep it up to date. You are responsible for safeguarding your login credentials and for all activity that occurs under your account. Notify us immediately at help@nothingtowearclub.ca if you suspect unauthorized use of your account.',
      },
      { h3: '3.3 Identity Verification' },
      {
        p:
          "We may require identity verification (for example, government-issued ID, a linked payment method, or a phone number) before you can publish a Listing or complete a Transaction, particularly for higher-value rentals and purchases. Verification reduces — but does not eliminate — the risk of fraudulent activity, and does not make us a guarantor of any User's identity or conduct.",
      },
    ],
  },
  {
    number: '4',
    title: 'Our Role: A Peer-to-Peer Marketplace',
    blocks: [
      {
        boldLead: 'Nothing To Wear is a marketplace, not a party to your Transaction.',
        p:
          ' We provide the technology that lets Lenders/Sellers and Renters/Buyers find each other, communicate, agree on terms, and pay for rentals and purchases. Unless a Listing is expressly identified as sold directly by us, the rental or purchase contract for each item is formed directly between the Lender/Seller and the Renter/Buyer. We are not the owner, lender, seller, buyer, renter, bailee, custodian, or insurer of any item listed on the Platform, and we do not inspect items before listing except where we state otherwise.',
      },
      {
        p:
          'Because we are not a party to the underlying rental or sale, we do not control, and cannot guarantee, the quality, safety, legality, authenticity, or condition of any item, or the truthfulness or performance of any User. We encourage Users to communicate clearly, review Listing photos and descriptions carefully, and use in-Platform messaging and payment tools (rather than off-platform arrangements) so that a record of the Transaction exists.',
      },
      {
        p:
          'Nothing in these Terms creates an employment, partnership, joint venture, or agency relationship between us and any User, except to the limited extent we expressly act as a collection agent for payments or as required under tax law described in Section 8.',
      },
    ],
  },
  {
    number: '5',
    title: 'Creating Listings (Lenders and Sellers)',
    blocks: [
      { h3: '5.1 Accuracy' },
      { p: 'If you list an item, you represent and warrant that:' },
      {
        list: [
          'you own the item or have the legal right to lend or sell it;',
          'the item is authentic, legally owned, not stolen, counterfeit, or subject to a third-party claim, lien, or security interest;',
          'your description, photographs, sizing, and condition notes are accurate and current, including any flaws, alterations, or repairs; and',
          'the item complies with all applicable Canadian product-safety and labelling requirements.',
        ],
      },
      { h3: '5.2 Pricing and Availability' },
      {
        p:
          'You are solely responsible for setting your rental or sale price, availability calendar, and any rental-specific terms (such as care instructions or a cleaning requirement). Prices displayed to Buyers/Renters will include our applicable service fee and disclosed taxes before checkout is completed, consistent with Section 10.',
      },
      { h3: '5.3 Removal of Listings' },
      {
        p:
          'We may remove, suspend, or decline to publish a Listing at our discretion — including where we reasonably believe it is inaccurate, unsafe, infringing, prohibited under Section 11, or otherwise inconsistent with these Terms — and will make reasonable efforts to notify the Lender/Seller of the reason.',
      },
    ],
  },
  {
    number: '6',
    title: 'Rental Transactions',
    blocks: [
      { h3: '6.1 Booking and the Rental Period' },
      {
        p:
          'When a Renter books an item, the Rental Period, pickup/delivery method, and return date shown at checkout become part of the binding agreement between the Lender and the Renter. Renters must return items in the condition received (ordinary wear excepted) by the end of the Rental Period, using the return method specified in the Listing.',
      },
      { h3: '6.2 Buyer Protection Fee' },
      {
        p:
          'Every rental includes a mandatory Buyer Protection Fee equal to 5% of the rental price, disclosed and charged at checkout. This fee is not optional and applies to every rental Transaction. The Buyer Protection Fee covers minor accidental damage to a garment beyond ordinary wear; it does not excuse or cover larger accidental damage, which remains subject to the Renter’s liability for damage or loss under Section 6.4 and the Renter & Buyer Policy. This fee does not make us an insurer.',
      },
      { h3: '6.3 Care of Rented Items' },
      {
        p:
          "Renters must use rented items only for their intended purpose, follow any care instructions provided in the Listing, and not alter, sublet, or permit a third party to use a rented item without the Lender's consent.",
      },
      { h3: '6.4 Late Returns, Loss, and Damage' },
      {
        p:
          'If an item is returned late, lost, or damaged beyond ordinary wear, the Renter may be charged additional fees, repair costs, or replacement costs, calculated using the methodology and liability caps set out in our Renter & Buyer Policy (Section 9), and charged to the payment method on file. Lenders and Renters are expected to inspect items and exchange photos at check-in and check-out, and to try to resolve a disputed claim between themselves through in-app messaging first, consistent with the Renter & Buyer Policy and Lender & Seller Policy; where that isn\'t possible, either party can escalate to help@nothingtowearclub.ca.',
      },
    ],
  },
  {
    number: '7',
    title: 'Purchase Transactions',
    blocks: [
      { h3: '7.1 Condition of Items' },
      {
        p:
          'Unless a Listing states an item is new with tags, purchased items are pre-owned and sold in the condition described and photographed in the Listing. Sellers must disclose material flaws (stains, tears, missing embellishments, alterations, odour) known to them.',
      },
      { h3: '7.2 Formation of Sale' },
      {
        p:
          "A binding sale contract is formed between the Seller and the Buyer when the Buyer's payment is authorized and the Seller accepts the order (or, where Listings are set to auto-accept, upon payment authorization). Risk of loss for a purchased item passes to the Buyer on delivery or pickup.",
      },
      { h3: '7.3 Returns and Refunds on Purchases' },
      {
        p:
          'Because items are pre-owned and sold peer-to-peer, sales are generally final once the Buyer has had a reasonable opportunity to inspect the item, except where:',
      },
      {
        list: [
          'the item received materially differs from its Listing description or photographs ("not as described"); or',
          'a return or refund right applies under the BPCPA or another applicable consumer-protection law, or was voluntarily offered by the Seller and disclosed at checkout.',
        ],
      },
      {
        p:
          "Buyers must report a \"not as described\" claim within 3 business days of delivery, with photo evidence, through the Platform's resolution centre. We will review evidence from both parties and may authorize a refund from the held payment, facilitate a return, or make a good-faith determination; this process does not limit either party's right to pursue other remedies available at law.",
      },
    ],
  },
  {
    number: '8',
    title: 'Fees, Payments, and Taxes',
    blocks: [
      { h3: '8.1 Service Fees' },
      {
        p:
          "Nothing To Wear charges a service fee to Renters/Buyers, Lenders/Sellers, or both, as disclosed before checkout is completed. All fees are shown in Canadian dollars and itemized separately from the item price prior to payment, consistent with BC's distance-sales disclosure requirements.",
      },
      { h3: '8.2 Payment Processing' },
      {
        p:
          'All payments are processed through our third-party payment processor, Stripe ("Payment Processor"). We do not store your full payment card details. By transacting on the Platform, you also agree to the Payment Processor\'s applicable terms. Funds owed to a Lender/Seller are released after the conditions disclosed for that Transaction type (e.g., confirmed delivery, expiry of a claims window) are met.',
      },
      { h3: '8.3 Taxes' },
      {
        p:
          "Prices may be subject to GST and applicable provincial sales tax. For rentals, the applicable provincial tax is based on the pickup/drop-off location; for purchases, it is based on the shipping address. Where we are required to do so under the Excise Tax Act's digital-economy and platform-facilitator rules, or under BC's marketplace-facilitator PST rules, we will calculate, collect, and remit the applicable tax on Transactions we facilitate. Where a Lender or Seller is independently registered for GST/HST or PST, they remain responsible for their own registration, collection, and remittance obligations on their sales, and for reporting rental or sale income to the Canada Revenue Agency. Nothing in these Terms constitutes tax advice, and Users should consult a qualified tax professional regarding their own obligations.",
      },
      { h3: '8.4 Currency and Failed Payments' },
      {
        p:
          'All amounts on the Platform are in Canadian dollars (CAD) unless stated otherwise. If a payment fails or is reversed (including a chargeback later found to be unwarranted), we may suspend the related Transaction, recover the amount owed from another payment method on file, or restrict your account pending resolution.',
      },
    ],
  },
  {
    number: '9',
    title: 'Shipping, Delivery, and Local Pickup',
    blocks: [
      {
        p:
          'Depending on the Listing, items may be exchanged by courier shipment or local pickup/drop-off arranged between the Lender/Seller and Renter/Buyer through the Platform. Where shipping is used, the party responsible for shipping cost, carrier selection, and packaging will be disclosed in the Listing. Users are responsible for packaging rental items securely enough to prevent damage in transit. We are not responsible for delays, loss, or damage caused by a shipping carrier, but will reasonably assist Users in pursuing a carrier claim where insurance was purchased through the Platform.',
      },
    ],
  },
  {
    number: '10',
    title: 'Cancellations, Refunds, and Your Right to Cancel',
    blocks: [
      {
        p:
          'Before you complete checkout, the Platform will clearly disclose: the total price (including our service fee and applicable taxes), the Rental Period or delivery estimate, and the applicable cancellation, return, and refund policy for that Listing type. You will have the opportunity to review the full order summary before confirming payment.',
      },
      { h3: '10.1 Renter/Buyer-Initiated Cancellations' },
      {
        p:
          'To cancel a booking or order, contact us directly at help@nothingtowearclub.ca; cancellations are handled by our support team rather than automatically through checkout. Where this process would otherwise conflict with a mandatory cancellation right available to you under the BPCPA or other applicable law, the legally mandated right prevails.',
      },
      { h3: '10.2 Lender/Seller-Initiated Cancellations' },
      {
        p:
          "Lenders and Sellers are asked to give at least 14 days' notice before cancelling any future booking on a Listing, so the affected Renter/Buyer has time to find an alternative — further detail is in our Lender & Seller Policy. If a Lender or Seller cancels a confirmed Transaction (other than for a reason attributable to the Renter/Buyer) without that notice, the Renter/Buyer will receive a full refund of amounts paid for that Transaction.",
      },
      { h3: '10.3 Platform-Initiated Cancellations' },
      {
        p:
          'We may cancel or reverse a Transaction where we reasonably suspect fraud, a safety issue, or a violation of these Terms, and will refund amounts paid to the extent appropriate in the circumstances.',
      },
    ],
  },
  {
    number: '11',
    title: 'User Conduct and Prohibited Items',
    blocks: [
      { h3: '11.1 Prohibited Conduct' },
      { p: 'You agree not to:' },
      {
        list: [
          'list or transact outside the Platform to avoid fees, verification, or dispute protections ("off-platform circumvention");',
          'post false, misleading, or deceptive Listings or reviews;',
          'engage in harassment, discrimination, or abusive conduct toward other Users;',
          'use the Platform for money laundering or any unlawful purpose;',
          "attempt to access another User's account or interfere with the security or normal operation of the Platform; or",
          'scrape, reverse-engineer, or use automated means to access the Platform except through our published APIs, if any.',
        ],
      },
      { h3: '11.2 Prohibited Items' },
      {
        p:
          "You may not list or exchange counterfeit goods, items subject to a recall or safety notice, stolen property, items that infringe a third party's intellectual property rights, fur or exotic animal products where prohibited by law, or any item that cannot lawfully be rented or sold in Canada.",
      },
    ],
  },
  {
    number: '12',
    title: 'User Content and Intellectual Property',
    blocks: [
      { h3: '12.1 Your Content' },
      {
        p:
          'You retain ownership of the Content you submit (including Listing photos and descriptions). By posting Content, you grant us a non-exclusive, royalty-free, worldwide, sublicensable licence to host, reproduce, display, and distribute that Content solely to operate, promote, and improve the Platform. You are responsible for ensuring you have the necessary rights to any Content you upload, including photographs you did not take yourself.',
      },
      { h3: '12.2 Our Intellectual Property' },
      {
        p:
          'The Platform\'s software, design, trademarks (including "Nothing To Wear"), and underlying technology are owned by us or our licensors and are protected under Canadian and international intellectual property law. Except for the limited licence to use the Platform for its intended purpose, no rights are granted to you.',
      },
      { h3: '12.3 Copyright Complaints' },
      {
        p:
          'If you believe Content on the Platform infringes your copyright, contact us at help@nothingtowearclub.ca with the information needed to identify the material and your rights, and we will review and respond in accordance with applicable Canadian copyright law.',
      },
    ],
  },
  {
    number: '13',
    title: 'Verification, Ratings, and Trust & Safety',
    blocks: [
      {
        p:
          'Ratings, reviews, and any "verified" badges are intended to help Users make informed decisions but are not a guarantee of a User\'s reliability, honesty, or an item\'s condition. We may use automated and manual review to detect fraud or policy violations, but we do not warrant that all bad actors or inaccurate Listings will be identified or removed.',
      },
    ],
  },
  {
    number: '14',
    title: 'Insurance and Risk',
    blocks: [
      {
        p:
          "The Buyer Protection Fee and any additional optional insurance offered through the Platform are described in full, including coverage limits and exclusions, at checkout. The Buyer Protection Fee is mandatory and applies automatically to every rental; it does not make us an insurer, and coverage is subject to the terms of the specific product and, where applicable, the terms of our third-party insurance or protection-plan provider. Users are encouraged to review their own homeowner's, renter's, or personal property insurance for any gaps.",
      },
    ],
  },
  {
    number: '15',
    title: 'Privacy',
    blocks: [
      {
        p:
          'We collect, use, and disclose personal information in accordance with our Privacy Policy, which is incorporated into these Terms. Our privacy practices are designed to comply with the federal Personal Information Protection and Electronic Documents Act (PIPEDA) and, for our operations in this province, British Columbia\'s Personal Information Protection Act (PIPA). By using the Platform, you consent to the collection, use, and disclosure of your personal information as described in the Privacy Policy, including sharing necessary contact and delivery details between a Lender/Seller and Renter/Buyer to complete a Transaction.',
      },
    ],
  },
  {
    number: '16',
    title: 'Electronic Communications and Marketing',
    blocks: [
      {
        p:
          "By creating an account, you consent to receive transactional messages (such as booking confirmations, return reminders, and account notices) from us electronically. Marketing or promotional messages will only be sent with your consent as required by Canada's Anti-Spam Legislation (CASL), will identify us and our contact information, and will include a functioning unsubscribe mechanism. You may withdraw marketing consent at any time without affecting your ability to receive transactional messages or otherwise use the Platform.",
      },
    ],
  },
  {
    number: '17',
    title: 'Disclaimers and Limitation of Liability',
    blocks: [
      { h3: '17.1 "As Available" Basis' },
      {
        p:
          'The Platform is provided on an "as is" and "as available" basis. To the fullest extent permitted by law, we disclaim all warranties, express or implied, regarding the Platform and any item listed on it, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. This disclaimer does not exclude or limit any warranty, condition, or right that cannot lawfully be excluded under the BPCPA, the BC Sale of Goods Act, or other applicable Canadian consumer-protection legislation, including in respect of an unfair contract term.',
      },
      { h3: '17.2 Limitation of Liability' },
      {
        p:
          'To the fullest extent permitted by law, and except for liability arising from our own gross negligence, wilful misconduct, or a right that cannot be limited under applicable consumer-protection law, our total liability to you for any claim arising out of or relating to the Platform will not exceed the greater of (a) the fees we received from you in connection with the Transaction giving rise to the claim, or (b) CAD $100. We are not liable for indirect, incidental, special, or consequential damages, or for the acts or omissions of another User.',
      },
    ],
  },
  {
    number: '18',
    title: 'Indemnification',
    blocks: [
      {
        p:
          'You agree to indemnify and hold harmless Nothing To Wear, our officers, directors, employees, and agents from any claim, loss, or expense (including reasonable legal fees) arising from your breach of these Terms, your Listing or use of an item, or your violation of any law or third-party right, except to the extent caused by our own negligence or wilful misconduct.',
      },
    ],
  },
  {
    number: '19',
    title: 'Suspension and Termination',
    blocks: [
      {
        p:
          'You may close your account at any time, subject to completing any Transactions already in progress. We may suspend or terminate your account, with notice where reasonably practicable, if you breach these Terms, pose a risk to other Users, or if required by law. Sections that by their nature should survive termination (including Sections 12, 17, 18, 20, and 21) will continue to apply.',
      },
    ],
  },
  {
    number: '20',
    title: 'Dispute Resolution',
    blocks: [
      { h3: '20.1 Direct Resolution' },
      {
        p:
          "Most issues between Users (such as damage claims or \"not as described\" disputes) should first be raised through the Platform's in-app resolution tools. For a complaint about the Platform itself, contact us at help@nothingtowearclub.ca and we will attempt to resolve it informally within 14 business days.",
      },
      { h3: '20.2 Your Options if Unresolved' },
      {
        p:
          "If a dispute involving us cannot be resolved informally, you may bring a claim in the British Columbia Civil Resolution Tribunal (which has jurisdiction over many consumer and small claims disputes in BC) or in the applicable British Columbia court. Consistent with the BPCPA, nothing in these Terms requires you to submit a claim valued at or under the BPCPA's low-value-claim threshold to private arbitration, or prevents you from participating in a class proceeding in respect of such a claim. For claims above that threshold, we may propose mediation or arbitration, but only on a basis you agree to at the time the dispute arises, not as a condition imposed in advance.",
      },
    ],
  },
  {
    number: '21',
    title: 'Governing Law and Jurisdiction',
    blocks: [
      {
        p:
          'These Terms are governed by the laws of British Columbia and the federal laws of Canada applicable in that province, without regard to conflict-of-law principles. Subject to Section 20, you and we each attorn to the non-exclusive jurisdiction of the courts of British Columbia.',
      },
    ],
  },
  {
    number: '22',
    title: 'Changes to These Terms',
    blocks: [
      {
        p:
          'We may update these Terms from time to time to reflect changes in our Services or applicable law. Where a change is material, we will provide advance notice (for example, by email or an in-app notice) before it takes effect and, where required by law, seek your renewed acceptance. Continued use of the Platform after a change takes effect constitutes acceptance of the updated Terms; if you do not agree, you must stop using the Platform and may close your account.',
      },
    ],
  },
  {
    number: '23',
    title: 'General Provisions',
    blocks: [
      { h3: '23.1 Entire Agreement' },
      {
        p:
          'These Terms, together with our Privacy Policy and any Transaction-specific disclosures, constitute the entire agreement between you and us regarding the Platform, and supersede any prior agreements on that subject.',
      },
      { h3: '23.2 Severability' },
      {
        p:
          'If any provision of these Terms is found unenforceable, that provision will be limited or severed to the minimum extent necessary, and the remaining provisions will stay in full force.',
      },
      { h3: '23.3 No Waiver' },
      { p: 'Our failure to enforce a provision of these Terms is not a waiver of our right to do so later.' },
      { h3: '23.4 Assignment' },
      {
        p:
          'You may not assign these Terms without our prior written consent. We may assign these Terms in connection with a merger, acquisition, or sale of assets, or by operation of law.',
      },
      { h3: '23.5 Force Majeure' },
      {
        p:
          'Neither party is liable for a failure to perform caused by events beyond its reasonable control, including natural disaster, labour dispute, or disruption to courier or payment-processing networks.',
      },
      { h3: '23.6 Language' },
      { p: 'These Terms are provided in English.' },
      { h3: '23.7 Notices' },
      {
        p:
          'We may provide notices to you via the email address or in-app notification associated with your account. You may provide notice to us at the contact details in Section 24.',
      },
    ],
  },
];

export const CONTACT = {
  number: '24',
  title: 'Contact Us',
  companyLine: 'Nothing To Wear Club Inc. (operating as "Nothing To Wear")',
  address: 'Unit #30, 5300 Main St, Kelowna, BC V1W 3W4',
  email: 'help@nothingtowearclub.ca',
};
